/*
 * Cart drawer behaviour that Dawn does not ship:
 *   <copy-code>    — copies a tier discount code to the clipboard
 *   <cart-upsells>  — fetches recommendations (Yespo's cart block or Shopify's) and
 *                     renders the row
 *
 * Both are re-created whenever the drawer re-renders (cart.js swaps
 * `.drawer__inner`'s innerHTML), so all state that must outlive a render lives in
 * module scope or sessionStorage rather than on the element.
 */

const UPSELL_DISMISS_KEY = 'parfium:cart-upsells-dismissed';
const upsellCache = new Map();

/*
 * Yespo's cart block recommends from the cart it last received in StatusCart, which
 * the Yespo app sends — not this file. After the cart changes, the request waits
 * ES_SETTLE_MS so that StatusCart reaches Yespo first. Results are kept per cart
 * (the set of product IDs in it), so a re-render for a quantity change re-uses them
 * and impressions go out once per cart, not once per re-render.
 */
const ES_SETTLE_MS = 2000;
const ES_GIVE_UP_MS = 10000;
const esCache = new Map();
const esImpressionsSent = new Set();
let lastCartSignature = null;
let cartChangedAt = 0;

/** `localStorage.esTrackingDebug = 1` logs every step. */
const esDebug = (...args) => {
  try {
    if (window.localStorage.getItem('esTrackingDebug')) console.log('[cartUpsells eSputnik]', ...args);
  } catch (error) {
    /* storage blocked */
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Resolves with window.eS once the Yespo app embed has defined it, or null. */
const whenYespoReady = () =>
  new Promise((resolve) => {
    if (typeof window.eS === 'function') return resolve(window.eS);
    const started = Date.now();
    const timer = setInterval(() => {
      if (typeof window.eS === 'function') {
        clearInterval(timer);
        resolve(window.eS);
      } else if (Date.now() - started > ES_GIVE_UP_MS) {
        clearInterval(timer);
        resolve(null);
      }
    }, 250);
  });

/** The product handle and variant a Yespo recommendation url points at. */
const parseProductUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    const variant = parsed.searchParams.get('variant');
    return { handle: decodeURIComponent(match[1]), variantId: variant && /^\d+$/.test(variant) ? variant : null };
  } catch (error) {
    return null;
  }
};

class CopyCode extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.feedback = this.querySelector('.cart-tiers__code-feedback');
    if (!this.button || this.button.dataset.bound) return;
    this.button.dataset.bound = 'true';
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  async onClick() {
    const code = this.dataset.code;
    if (!code) return;

    let copied = false;
    try {
      // navigator.clipboard needs a secure context; the theme preview is http.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        copied = true;
      } else {
        copied = this.legacyCopy(code);
      }
    } catch (error) {
      copied = this.legacyCopy(code);
    }

    this.classList.toggle('is-copied', copied);
    if (this.feedback) {
      this.feedback.textContent = copied
        ? window.cartDrawerStrings?.codeCopied || 'Copied'
        : window.cartDrawerStrings?.codeCopyFailed || code;
    }

    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => {
      this.classList.remove('is-copied');
      if (this.feedback) this.feedback.textContent = '';
    }, 2000);
  }

  legacyCopy(code) {
    const field = document.createElement('textarea');
    field.value = code;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.top = '-9999px';
    document.body.appendChild(field);
    field.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }
    document.body.removeChild(field);
    return ok;
  }
}
customElements.define('copy-code', CopyCode);

class CartUpsells extends HTMLElement {
  connectedCallback() {
    if (sessionStorage.getItem(UPSELL_DISMISS_KEY) === '1') return;

    this.track = this.querySelector('.cart-upsells__track');
    this.template = this.querySelector('.cart-upsells__template');
    this.slider = this.querySelector('.cart-upsells__slider');
    const dismiss = this.querySelector('.cart-upsells__dismiss');
    if (dismiss) dismiss.addEventListener('click', this.onDismiss.bind(this));

    this.load();
  }

  onDismiss() {
    sessionStorage.setItem(UPSELL_DISMISS_KEY, '1');
    this.hidden = true;
  }

  async load() {
    const productId = this.dataset.productId;
    if (!productId || !this.track || !this.template) return;

    const signature = this.cartSignature();
    if (signature !== lastCartSignature) {
      // The cart seen first on a page load was sent by the app on an earlier page;
      // only a change made on this page has to wait for StatusCart.
      if (lastCartSignature !== null) cartChangedAt = Date.now();
      lastCartSignature = signature;
    }

    const source = this.dataset.source || 'shopify';
    let products = [];
    let fromYespo = false;

    if (source !== 'shopify' && this.dataset.esVariantId) {
      // Ask Yespo only once the shopper actually opens the drawer.
      await this.whenDrawerOpen();
      if (!this.isConnected) return;
      products = await this.loadYespo(signature);
      if (!this.isConnected) return;
      fromYespo = products.length > 0;
    }

    if (!products.length && source !== 'esputnik_only') {
      products = upsellCache.get(productId);
      if (!products) {
        products = await this.fetchRecommendations(productId, 'complementary');
        if (!products.length) products = await this.fetchRecommendations(productId, 'related');
        upsellCache.set(productId, products);
      }
    }

    // Never recommend something already in the cart.
    const inCart = new Set(
      Array.from(document.querySelectorAll('[data-cart-product-id]')).map((el) => el.dataset.cartProductId)
    );
    const limit = parseInt(this.dataset.limit, 10) || 6;
    const usable = products.filter((product) => !inCart.has(String(product.id))).slice(0, limit);
    if (!usable.length) return;

    this.render(usable);
    if (fromYespo) this.markForTracking(usable, signature);
    this.hidden = false;
    // The swiper element was inert while empty — let it pick up the new slides.
    if (this.slider?.swiper) this.slider.swiper.update();
  }

  /** The products in the cart as rendered — what the Yespo cart block depends on. */
  cartSignature() {
    const scope = this.closest('cart-drawer') || document;
    const ids = Array.from(scope.querySelectorAll('[data-cart-product-id]'), (el) => el.dataset.cartProductId);
    return Array.from(new Set(ids)).sort().join(',');
  }

  whenDrawerOpen() {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.classList.contains('active')) return Promise.resolve();
    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (!drawer.classList.contains('active')) return;
        observer.disconnect();
        resolve();
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /** Yespo's cart block → product JSON objects, each carrying its Yespo item as `es`. */
  loadYespo(signature) {
    if (esCache.has(signature)) return esCache.get(signature);

    const request = (async () => {
      const settle = cartChangedAt + ES_SETTLE_MS - Date.now();
      if (settle > 0) await wait(settle);

      const eS = await whenYespoReady();
      if (!eS) {
        esDebug('window.eS never appeared (Yespo embed not loaded)');
        return [];
      }

      const params = { variantId: this.dataset.esVariantId };
      esDebug('request', params);
      const items = await new Promise((resolve) => {
        try {
          eS('getRecommendations', params, (error, result) => {
            if (error) esDebug('Yespo error', error);
            resolve(!error && Array.isArray(result) ? result : []);
          });
        } catch (error) {
          esDebug('eS threw', error);
          resolve([]);
        }
      });
      esDebug(`Yespo returned ${items.length} item(s)`, items);

      const products = await Promise.all(
        items.map((item) => {
          const target = item && item.product_id && item.url ? parseProductUrl(item.url) : null;
          if (!target) return null;
          // Yespo's url is kept as is — it carries sc_content, which is how clicks are counted.
          return this.fetchProduct(target.handle).then((product) =>
            product ? { ...product, url: item.url, preferredVariantId: target.variantId, es: item } : null
          );
        })
      );
      return products.filter(Boolean);
    })();

    // An empty answer is not kept: StatusCart may just not have arrived yet.
    esCache.set(signature, request);
    request.then((products) => {
      if (!products.length) esCache.delete(signature);
    });
    return request;
  }

  async fetchProduct(handle) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * The recommendation contract esputnik-events.js reads (.claude/rules/esputnik-tracking.md):
   * it sends ProductImpressions once the row is in view, and the extra ProductPage for a
   * "+" click ([data-es-buy]) inside it.
   */
  markForTracking(products, signature) {
    const slides = this.track.querySelectorAll('.cart-upsells__slide');
    products.forEach((product, index) => {
      if (!slides[index] || !product.es) return;
      slides[index].dataset.esRecommendationProduct = String(product.es.product_id);
      slides[index].dataset.esContainerType = String(product.es.container_type || '');
    });

    this.setAttribute('data-es-recommendations', '');
    this.dataset.esContainerType = String(products[0].es?.container_type || '');

    // A quantity change re-renders the drawer with the same cart — one impression per cart.
    if (esImpressionsSent.has(signature)) {
      this.dataset.esImpressionsBound = 'true';
      return;
    }
    esImpressionsSent.add(signature);
    window.esTracking?.trackRecommendations?.(this);
  }

  async fetchRecommendations(productId, intent) {
    const base = this.dataset.url || '/recommendations/products';
    const limit = this.dataset.limit || 6;
    const url = `${base}.json?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=${intent}`;
    try {
      const response = await fetch(url);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      return [];
    }
  }

  render(products) {
    const fragment = document.createDocumentFragment();

    products.forEach((product) => {
      const slide = this.template.content.cloneNode(true);
      const preferred = product.variants?.find(
        (candidate) => candidate.available && String(candidate.id) === String(product.preferredVariantId)
      );
      const variant =
        preferred || product.variants?.find((candidate) => candidate.available) || product.variants?.[0];

      slide.querySelectorAll('[data-upsell-link]').forEach((link) => {
        link.setAttribute('href', product.url);
      });

      const image = slide.querySelector('[data-upsell-image]');
      if (image) {
        if (product.featured_image) {
          image.src = this.sizedImage(product.featured_image, 252);
          image.alt = product.title || '';
        } else {
          image.remove();
        }
      }

      this.setText(slide, '[data-upsell-vendor]', product.vendor);
      this.setText(slide, '[data-upsell-title]', product.title);
      this.setText(slide, '[data-upsell-subtitle]', product.type);

      const price = variant ? variant.price : product.price;
      const compareAt = variant ? variant.compare_at_price : product.compare_at_price;
      this.setText(slide, '[data-upsell-price]', this.money(price));
      const rrpPrefix = window.cartDrawerStrings?.rrpPrefix || '';
      this.setText(
        slide,
        '[data-upsell-rrp]',
        compareAt && compareAt > price ? `${rrpPrefix} ${this.money(compareAt)}`.trim() : ''
      );

      const add = slide.querySelector('[data-upsell-add]');
      if (add) {
        if (variant && variant.available) {
          add.dataset.variantId = variant.id;
          add.addEventListener('click', this.onAdd.bind(this));
        } else {
          add.disabled = true;
        }
      }

      fragment.appendChild(slide);
    });

    this.track.replaceChildren(fragment);
  }

  setText(scope, selector, value) {
    const el = scope.querySelector(selector);
    if (!el) return;
    if (value) {
      el.textContent = value;
    } else {
      el.remove();
    }
  }

  sizedImage(src, width) {
    // Shopify CDN urls accept a &width= param; leave anything else untouched.
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  money(cents) {
    if (typeof cents !== 'number') return '';
    const format = window.cartDrawerStrings?.moneyFormat;
    if (!format) return (cents / 100).toFixed(2);
    // shop.money_format may use any of amount, amount_no_decimals,
    // amount_with_comma_separator, amount_no_decimals_with_comma_separator, …
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      const noDecimals = token.includes('no_decimals');
      const comma = token.includes('comma_separator');
      const value = (cents / 100).toFixed(noDecimals ? 0 : 2);
      const [int, dec] = value.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
      if (!dec) return grouped;
      return `${grouped}${comma ? ',' : '.'}${dec}`;
    });
  }

  async onAdd(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const drawer = document.querySelector('cart-drawer');
    button.disabled = true;
    button.classList.add('is-loading');

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    if (drawer) {
      body.append('sections', drawer.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const parsed = await response.json();
      if (parsed.status) throw new Error(parsed.description || parsed.message);
      if (drawer) drawer.renderContents(parsed);
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}
customElements.define('cart-upsells', CartUpsells);
